import { useRef, type KeyboardEvent } from 'react';
import { Star } from 'lucide-react';
import { formatRelativeTime } from '../../lib/mailFormat';
import type { MailThreadSummaryDto } from '../../lib/mailTypes';

function participantsLabel(participants: string[]): string {
  if (participants.length === 0) return '(no sender)';
  const [first, second] = participants;
  return participants.length > 2
    ? `${first}, ${second} +${participants.length - 2}`
    : participants.join(', ');
}

export function MailThreadList({
  threads,
  selectedId,
  loading,
  loadingMore,
  error,
  hasMore,
  onSelect,
  onToggleStar,
  onLoadMore,
  onRetry,
}: {
  threads: MailThreadSummaryDto[];
  selectedId: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  onSelect: (id: string) => void;
  onToggleStar: (thread: MailThreadSummaryDto) => void;
  onLoadMore: () => void;
  onRetry: () => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);

  /** j / ArrowDown and k / ArrowUp move focus between rows; Enter opens (native button). */
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const down = e.key === 'j' || e.key === 'ArrowDown';
    const up = e.key === 'k' || e.key === 'ArrowUp';
    if (!down && !up) return;
    const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-thread-row]') ?? []);
    if (rows.length === 0) return;
    const at = rows.indexOf(document.activeElement as HTMLElement);
    const next = down ? Math.min(rows.length - 1, at + 1) : Math.max(0, at < 0 ? 0 : at - 1);
    e.preventDefault();
    rows[next]?.focus();
  };

  if (loading) return <div className="mail-list-state" role="status">Loading messages…</div>;

  return (
    <div className="mail-list">
      {error && (
        <div role="alert" className="error-banner">
          {error}{' '}
          <button type="button" className="btn btn-xs" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
      {!error && threads.length === 0 && <div className="mail-list-state empty-state">No conversations here.</div>}
      {threads.length > 0 && (
        <ul ref={listRef} className="mail-thread-list" onKeyDown={onKeyDown} aria-label="Conversations">
          {threads.map((t) => (
            <li
              key={t.id}
              className={[
                'mail-thread-row',
                t.unread ? 'unread' : '',
                t.id === selectedId ? 'selected' : '',
              ].join(' ').trim()}
              data-unread={t.unread ? 'true' : 'false'}
            >
              <button
                type="button"
                className="mail-star"
                aria-label={t.starred ? 'Remove star' : 'Add star'}
                aria-pressed={t.starred}
                onClick={() => onToggleStar(t)}
              >
                <Star size={16} fill={t.starred ? 'currentColor' : 'none'} aria-hidden />
              </button>
              <button
                type="button"
                data-thread-row
                className="mail-thread-open"
                aria-current={t.id === selectedId ? 'true' : undefined}
                onClick={() => onSelect(t.id)}
              >
                <span className="mail-thread-top">
                  <span className="mail-thread-from">
                    {participantsLabel(t.participants)}
                    {t.messageCount > 1 && <span className="cell-sub"> ({t.messageCount})</span>}
                  </span>
                  <span className="mail-thread-time">{formatRelativeTime(t.lastMessageAt)}</span>
                </span>
                <span className="mail-thread-subject">{t.subject || '(no subject)'}</span>
                <span className="mail-thread-snippet">{t.snippet}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <div className="mail-load-more">
          <button type="button" className="btn btn-xs" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
